import './Tabs.scss';

import classnames from 'classnames';
import {
  any,
  arrayOf,
  bool,
  func,
  node,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import qs from 'query-string';
import React from 'react';
import { withRouter } from 'react-router';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

const ROUTE_PREFIX = 'currentTab-';

class Tabs extends React.Component {
  static isAValidTab(tabId, tabs) {
    return tabs.some(tab => tabId === tab.value && !tab.disabled);
  }

  static getActiveTabId(props) {
    const { tabs, enableRouteBasedState, defaultActive } = props;
    const tabIdFromRoute = Tabs.getTabIdFromRoute(props);
    if (enableRouteBasedState && Tabs.isAValidTab(tabIdFromRoute, tabs)) {
      return tabIdFromRoute;
    }
    if (Tabs.isAValidTab(defaultActive, tabs)) {
      return defaultActive;
    }
    const firstEnabledTab = tabs.find(tab => !tab.disabled);

    return firstEnabledTab?.value || tabs[0].value;
  }

  static getTabIdFromRoute(props) {
    const { id, location } = props;
    return qs.parse(location.search)[`${ROUTE_PREFIX}${id}`];
  }

  static getDerivedStateFromProps(props, state) {
    // Change active tab only on location updates
    if (props.location !== state.location) {
      return {
        activeTabId: Tabs.getActiveTabId(props),
        location: props.location,
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    const activeTabId = Tabs.getActiveTabId(this.props);
    this.state = { activeTabId, location: props.location };
    if (props.enableRouteBasedState) this.updateRoute(activeTabId, true);
  }

  getActiveClassName(tabId) {
    return this.state.activeTabId === tabId ? 'active' : '';
  }

  setActive = e => {
    const { onTabChange, enableRouteBasedState } = this.props;
    const activeTabId = e.target.dataset.tabId;
    this.setState({ activeTabId }, () => {
      onTabChange(activeTabId);
      if (enableRouteBasedState) this.updateRoute(activeTabId);
    });
  };

  updateRoute(newTabId, shouldReplace) {
    const { location, history, id } = this.props;
    const search = qs.parse(location.search);
    const action = shouldReplace ? 'replace' : 'push';
    history[action]({
      pathname: location.pathname,
      search: qs.stringify({ ...search, [`${ROUTE_PREFIX}${id}`]: newTabId }),
    });
  }

  render() {
    const { tabs, children } = this.props;
    const { activeTabId } = this.state;
    const activeTab = tabs.find(tab => tab.value === activeTabId);
    return (
      <div>
        <Nav tabs>
          {tabs.map(tab => {
            const { value, disabled, color, label } = tab;
            const colorClassName = color ? `nav-link-${color}` : undefined;
            return (
              <NavItem key={value}>
                <NavLink
                  data-tab-id={value}
                  disabled={disabled}
                  className={classnames(
                    this.getActiveClassName(value),
                    colorClassName,
                  )}
                  onClick={this.setActive}
                >
                  {label}
                </NavLink>
              </NavItem>
            );
          })}
          {children && (
            <NavItem className="ml-auto">
              <div className="align-items-center">{children}</div>
            </NavItem>
          )}
        </Nav>
        <TabContent activeTab={activeTabId}>
          {activeTab && (
            <TabPane key={activeTab.value} tabId={activeTab.value}>
              {activeTab.content}
            </TabPane>
          )}
        </TabContent>
      </div>
    );
  }
}

Tabs.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  defaultActive: string,
  tabs: arrayOf(
    shape({
      label: oneOfType([node, string]).isRequired,
      value: string.isRequired,
      disabled: bool,
      content: any, // eslint-disable-line react/forbid-prop-types
      color: string,
    }).isRequired,
  ).isRequired,
  children: node,
  onTabChange: func,
  id: string.isRequired,
  enableRouteBasedState: bool,
  location: shape({ search: string.isRequired, pathname: string.isRequired })
    .isRequired,
  history: shape({ push: func.isRequired }).isRequired,
};

Tabs.defaultProps = {
  defaultActive: undefined,
  children: undefined,
  onTabChange: () => {},
  enableRouteBasedState: true,
};

export default withRouter(Tabs);
